import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Container } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClipboardList,
  faHeartPulse,
  faAllergies,
  faScissors,
  faChartLine,
  faPlus,
  faTrash,
  faPen,
  faCheck,
  faExclamationTriangle,
  faUtensils,
  faSeedling,
  faTimes,
  faCalendarDays,
  faStethoscope,
  faBell,
  faLayerGroup,
  faBrain,
  faLungs,
  faEye,
  faBone,
  faShieldHalved,
  faChevronDown,
  faChevronUp,
  faClock,
  faCircleCheck,
  faListCheck,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import symptomsService, {
  Symptom,
  SymptomLog,
  MedicalCondition,
  Allergy,
  PastSurgery,
  DietaryRestriction,
  FoodLog,
} from '../../services/symptomsService';
import reminderService, {
  Reminder,
  ReminderPayload,
} from '../../services/reminderService';
import './SymptomsChecker.css';
import BackButton from '../../components/BackButton/BackButton';
import { useProfile } from '../../context/ProfileContext';

// ─── Types ────────────────────────────────────────────────────────────

type PrimaryTab = 'tracker' | 'diet' | 'reminders';
type TrackerSubTab = 'symptoms' | 'conditions' | 'allergies' | 'surgeries' | 'tracking' | 'categories';
type DietSubTab = 'food_log' | 'restrictions';
type ReminderSubTab = 'all' | 'upcoming' | 'today' | 'completed';

interface ToastState {
  message: string;
  type: 'success' | 'error';
}

// ─── Tracker Sub-Tab Config ───────────────────────────────────────────

interface TabConfigItem {
  key: TrackerSubTab;
  label: string;
  icon: IconDefinition;
  color: string;
  headerClass: string;
  iconClass: string;
  headerTitle: string;
  headerDesc: string;
}

const TRACKER_TAB_CONFIG: TabConfigItem[] = [
  {
    key: 'symptoms',
    label: 'Symptoms',
    icon: faClipboardList,
    color: '#ef4444',
    headerClass: 'sc-tab-header-symptoms',
    iconClass: 'sc-icon-symptoms',
    headerTitle: 'My Symptoms',
    headerDesc: 'Track and manage your symptoms. Log severity over time to identify patterns and share with your doctor.',
  },
  {
    key: 'conditions',
    label: 'Conditions',
    icon: faHeartPulse,
    color: '#ec4899',
    headerClass: 'sc-tab-header-conditions',
    iconClass: 'sc-icon-conditions',
    headerTitle: 'Medical Conditions',
    headerDesc: 'Record any diagnosed medical conditions, track their status, and maintain a complete health history.',
  },
  {
    key: 'allergies',
    label: 'Allergies',
    icon: faAllergies,
    color: '#f59e0b',
    headerClass: 'sc-tab-header-allergies',
    iconClass: 'sc-icon-allergies',
    headerTitle: 'Allergies',
    headerDesc: 'Manage your allergy records including type, severity, and reactions for easy reference.',
  },
  {
    key: 'surgeries',
    label: 'Surgeries',
    icon: faScissors,
    color: '#6366f1',
    headerClass: 'sc-tab-header-surgeries',
    iconClass: 'sc-icon-surgeries',
    headerTitle: 'Past Surgeries',
    headerDesc: 'Record your surgical history including dates, hospitals, and surgeons for a complete medical profile.',
  },
  {
    key: 'tracking',
    label: 'Tracking',
    icon: faChartLine,
    color: '#10b981',
    headerClass: 'sc-tab-header-tracking',
    iconClass: 'sc-icon-tracking',
    headerTitle: 'Symptom Tracking',
    headerDesc: 'Visualize how your symptoms change over time with severity charts and detailed log history.',
  },
  {
    key: 'categories',
    label: 'Categories',
    icon: faLayerGroup,
    color: '#8b5cf6',
    headerClass: 'sc-tab-header-categories',
    iconClass: 'sc-icon-categories',
    headerTitle: 'AI Categories',
    headerDesc: 'View your symptoms organized by health category using AI-powered classification.',
  },
];

// ─── Category Icon Map ──────────────────────────────────────────────────

const CATEGORY_ICONS: Record<string, { icon: IconDefinition; color: string; bg: string }> = {
  cardiovascular: { icon: faHeartPulse, color: '#ef4444', bg: 'rgba(239,68,68,0.1)' },
  respiratory: { icon: faLungs, color: '#06b6d4', bg: 'rgba(6,182,212,0.1)' },
  neurological: { icon: faBrain, color: '#8b5cf6', bg: 'rgba(139,92,246,0.1)' },
  musculoskeletal: { icon: faBone, color: '#f59e0b', bg: 'rgba(245,158,11,0.1)' },
  dermatological: { icon: faShieldHalved, color: '#ec4899', bg: 'rgba(236,72,153,0.1)' },
  ophthalmic: { icon: faEye, color: '#10b981', bg: 'rgba(16,185,129,0.1)' },
  gastrointestinal: { icon: faUtensils, color: '#f97316', bg: 'rgba(249,115,22,0.1)' },
  general: { icon: faClipboardList, color: '#64748b', bg: 'rgba(100,116,139,0.1)' },
  uncategorized: { icon: faClipboardList, color: '#94a3b8', bg: 'rgba(148,163,184,0.1)' },
};

const getCategoryMeta = (category: string) => {
  const key = category.toLowerCase();
  return CATEGORY_ICONS[key] || CATEGORY_ICONS['general'];
};

// ─── Main Component ───────────────────────────────────────────────────

const SymptomsChecker: React.FC = () => {
  const { t } = useTranslation(['services', 'common']);
  const { activeProfile } = useProfile();
  // Primary tab
  const [primaryTab, setPrimaryTab] = useState<PrimaryTab>('tracker');

  // Sub-tab states
  const [trackerSubTab, setTrackerSubTab] = useState<TrackerSubTab>('symptoms');
  const [dietSubTab, setDietSubTab] = useState<DietSubTab>('food_log');
  const [reminderSubTab, setReminderSubTab] = useState<ReminderSubTab>('all');

  // Deep-link support: /symptoms-checker?tab=conditions | ?tab=add
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (!tab) return;
    if (tab === 'conditions') setTrackerSubTab('conditions');
    else if (tab === 'add') setModalType('symptom_log');
    searchParams.delete('tab');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<ToastState | null>(null);

  // Data states
  const [symptoms, setSymptoms] = useState<Symptom[]>([]);
  const [symptomLogs, setSymptomLogs] = useState<SymptomLog[]>([]);
  const [conditions, setConditions] = useState<MedicalCondition[]>([]);
  const [allergies, setAllergies] = useState<Allergy[]>([]);
  const [surgeries, setSurgeries] = useState<PastSurgery[]>([]);
  const [dietaryRestrictions, setDietaryRestrictions] = useState<DietaryRestriction[]>([]);
  const [foodLogs, setFoodLogs] = useState<FoodLog[]>([]);

  // Reminders data
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [remindersLoading, setRemindersLoading] = useState(false);

  // AI Categories data
  const [categoryData, setCategoryData] = useState<Record<string, Symptom[]>>({});
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categorizingAI, setCategorizingAI] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  // Modal
  const [modalType, setModalType] = useState<string | null>(null);
  const [modalData, setModalData] = useState<Record<string, string>>({});
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<{ type: string; id: number } | null>(null);

  const showToast = useCallback((message: string, type: ToastState['type']) => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  // ─── Fetch All Data ──────────────────────────────────────────────────

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [s, sl, c, a, su, dr, fl] = await Promise.all([
        symptomsService.getSymptoms(),
        symptomsService.getSymptomLogs(),
        symptomsService.getMedicalConditions(),
        symptomsService.getAllergies(),
        symptomsService.getPastSurgeries(),
        symptomsService.getDietaryRestrictions(),
        symptomsService.getFoodLogs(),
      ]);
      setSymptoms(s);
      setSymptomLogs(sl);
      setConditions(c);
      setAllergies(a);
      setSurgeries(su);
      setDietaryRestrictions(dr);
      setFoodLogs(fl);
    } catch {
      showToast(t('services:symptomsChecker.failedToLoadData'), 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  const getDependantId = useCallback((): number | undefined => {
    if (activeProfile && activeProfile.type !== 'self' && typeof activeProfile.id === 'number') {
      return activeProfile.id;
    }
    return undefined;
  }, [activeProfile]);

  const fetchReminders = useCallback(async () => {
    try {
      setRemindersLoading(true);
      const data = await reminderService.getReminders(getDependantId());
      setReminders(data);
    } catch {
      showToast(t('services:symptomsChecker.failedToLoadReminders'), 'error');
    } finally {
      setRemindersLoading(false);
    }
  }, [showToast, getDependantId]);

  const fetchCategories = useCallback(async () => {
    try {
      setCategoriesLoading(true);
      const data = await symptomsService.getSymptomsByCategory();
      setCategoryData(data);
    } catch {
      showToast(t('services:symptomsChecker.failedToLoadCategories'), 'error');
    } finally {
      setCategoriesLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchAll(); }, [fetchAll, activeProfile]);

  useEffect(() => {
    if (primaryTab === 'reminders') fetchReminders();
  }, [primaryTab, fetchReminders, activeProfile]);

  useEffect(() => {
    if (primaryTab === 'tracker' && trackerSubTab === 'categories') fetchCategories();
  }, [primaryTab, trackerSubTab, fetchCategories, activeProfile]);

  // ─── Stats ──────────────────────────────────────────────────────────

  const trackerStats = useMemo(() => ({
    symptoms: symptoms.length,
    conditions: conditions.filter(c => c.status === 'active').length,
    allergies: allergies.length,
    surgeries: surgeries.length,
  }), [symptoms, conditions, allergies, surgeries]);

  const dietStats = useMemo(() => ({
    foodLogsToday: foodLogs.filter(f => {
      const today = new Date().toDateString();
      return new Date(f.logged_at).toDateString() === today;
    }).length,
    totalRestrictions: dietaryRestrictions.length,
    mealsLogged: foodLogs.length,
  }), [foodLogs, dietaryRestrictions]);

  const reminderStats = useMemo(() => ({
    total: reminders.length,
    upcoming: reminders.filter(r => r.status === 'upcoming').length,
    today: reminders.filter(r => r.status === 'today').length,
    completed: reminders.filter(r => r.is_completed).length,
  }), [reminders]);

  // ─── Modal Helpers ──────────────────────────────────────────────────

  const openModal = (type: string, data?: Record<string, string>, id?: number) => {
    setModalType(type);
    setModalData(data || {});
    setEditingId(id || null);
  };

  const closeModal = () => {
    setModalType(null);
    setModalData({});
    setEditingId(null);
  };

  const handleModalField = (field: string, value: string) => {
    setModalData(prev => ({ ...prev, [field]: value }));
  };

  // ─── CRUD Handlers ──────────────────────────────────────────────────

  const handleSave = async () => {
    try {
      setSaving(true);
      switch (modalType) {
        case 'symptom':
          if (editingId) {
            await symptomsService.updateSymptom(editingId, {
              name: modalData.name,
              description: modalData.description || '',
              severity: parseInt(modalData.severity || '5'),
              body_area: modalData.body_area || '',
              first_noticed: modalData.first_noticed || null,
            } as Partial<Symptom>);
          } else {
            await symptomsService.createSymptom({
              name: modalData.name,
              description: modalData.description || '',
              severity: parseInt(modalData.severity || '5'),
              body_area: modalData.body_area || '',
              first_noticed: modalData.first_noticed || null,
            } as Partial<Symptom>);
          }
          break;
        case 'symptom_log':
          await symptomsService.createSymptomLog({
            symptom: parseInt(modalData.symptom),
            severity: parseInt(modalData.severity || '5'),
            notes: modalData.notes || '',
          } as unknown as Partial<SymptomLog>);
          break;
        case 'condition':
          if (editingId) {
            await symptomsService.updateMedicalCondition(editingId, {
              name: modalData.name,
              diagnosed_date: modalData.diagnosed_date || null,
              status: modalData.status as MedicalCondition['status'] || 'active',
              notes: modalData.notes || '',
            } as Partial<MedicalCondition>);
          } else {
            await symptomsService.createMedicalCondition({
              name: modalData.name,
              diagnosed_date: modalData.diagnosed_date || null,
              status: modalData.status as MedicalCondition['status'] || 'active',
              notes: modalData.notes || '',
            } as Partial<MedicalCondition>);
          }
          break;
        case 'allergy':
          if (editingId) {
            await symptomsService.updateAllergy(editingId, {
              name: modalData.name,
              allergy_type: modalData.allergy_type as Allergy['allergy_type'] || 'other',
              severity: modalData.severity as Allergy['severity'] || 'moderate',
              reaction: modalData.reaction || '',
            } as Partial<Allergy>);
          } else {
            await symptomsService.createAllergy({
              name: modalData.name,
              allergy_type: modalData.allergy_type as Allergy['allergy_type'] || 'other',
              severity: modalData.severity as Allergy['severity'] || 'moderate',
              reaction: modalData.reaction || '',
            } as Partial<Allergy>);
          }
          break;
        case 'surgery':
          if (editingId) {
            await symptomsService.updatePastSurgery(editingId, {
              name: modalData.name,
              surgery_date: modalData.surgery_date || null,
              hospital: modalData.hospital || '',
              surgeon: modalData.surgeon || '',
              notes: modalData.notes || '',
            } as Partial<PastSurgery>);
          } else {
            await symptomsService.createPastSurgery({
              name: modalData.name,
              surgery_date: modalData.surgery_date || null,
              hospital: modalData.hospital || '',
              surgeon: modalData.surgeon || '',
              notes: modalData.notes || '',
            } as Partial<PastSurgery>);
          }
          break;
        case 'dietary':
          if (editingId) {
            await symptomsService.updateDietaryRestriction(editingId, {
              name: modalData.name,
              restriction_type: modalData.restriction_type as DietaryRestriction['restriction_type'] || 'preference',
              notes: modalData.notes || '',
            } as Partial<DietaryRestriction>);
          } else {
            await symptomsService.createDietaryRestriction({
              name: modalData.name,
              restriction_type: modalData.restriction_type as DietaryRestriction['restriction_type'] || 'preference',
              notes: modalData.notes || '',
            } as Partial<DietaryRestriction>);
          }
          break;
        case 'food_log':
          await symptomsService.createFoodLog({
            food_item: modalData.food_item,
            meal_type: modalData.meal_type as FoodLog['meal_type'] || 'lunch',
            notes: modalData.notes || '',
          } as Partial<FoodLog>);
          break;
        case 'reminder':
          if (editingId) {
            await reminderService.updateReminder(editingId, {
              title: modalData.title,
              description: modalData.description || '',
              reminder_datetime: modalData.reminder_datetime,
              category: modalData.category || 'general',
              priority: modalData.priority || 'medium',
              recurrence: modalData.recurrence || 'none',
              notes: modalData.notes || '',
            } as ReminderPayload);
          } else {
            await reminderService.createReminder({
              title: modalData.title,
              description: modalData.description || '',
              reminder_datetime: modalData.reminder_datetime,
              category: modalData.category || 'general',
              priority: modalData.priority || 'medium',
              recurrence: modalData.recurrence || 'none',
              notes: modalData.notes || '',
            } as ReminderPayload);
          }
          fetchReminders();
          break;
      }
      showToast(editingId ? t('services:symptomsChecker.updatedSuccessfully') : t('services:symptomsChecker.createdSuccessfully'), 'success');
      closeModal();
      if (modalType !== 'reminder') fetchAll();
    } catch {
      showToast(t('services:symptomsChecker.failedToSave'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      switch (confirmDelete.type) {
        case 'symptom': await symptomsService.deleteSymptom(confirmDelete.id); break;
        case 'symptom_log': await symptomsService.deleteSymptomLog(confirmDelete.id); break;
        case 'condition': await symptomsService.deleteMedicalCondition(confirmDelete.id); break;
        case 'allergy': await symptomsService.deleteAllergy(confirmDelete.id); break;
        case 'surgery': await symptomsService.deletePastSurgery(confirmDelete.id); break;
        case 'dietary': await symptomsService.deleteDietaryRestriction(confirmDelete.id); break;
        case 'food_log': await symptomsService.deleteFoodLog(confirmDelete.id); break;
        case 'reminder': await reminderService.deleteReminder(confirmDelete.id); fetchReminders(); break;
      }
      showToast(t('services:symptomsChecker.deletedSuccessfully'), 'success');
      setConfirmDelete(null);
      if (confirmDelete.type !== 'reminder') fetchAll();
    } catch {
      showToast(t('services:symptomsChecker.failedToDelete'), 'error');
    }
  };

  const handleCompleteReminder = async (id: number) => {
    try {
      await reminderService.markComplete(id);
      showToast(t('services:symptomsChecker.reminderMarkedComplete'), 'success');
      fetchReminders();
    } catch {
      showToast(t('services:symptomsChecker.failedToCompleteReminder'), 'error');
    }
  };

  const handleCategorizeAI = async () => {
    try {
      setCategorizingAI(true);
      const result = await symptomsService.categorizeSymptoms();
      showToast(`AI categorized ${result.categorized} of ${result.total} symptoms!`, 'success');
      fetchCategories();
    } catch {
      showToast('Failed to categorize symptoms.', 'error');
    } finally {
      setCategorizingAI(false);
    }
  };

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  };

  // ─── Tracking Chart Data ────────────────────────────────────────────

  const chartData = useMemo(() => {
    return symptomLogs
      .slice()
      .sort((a, b) => new Date(a.logged_at).getTime() - new Date(b.logged_at).getTime())
      .map(log => ({
        date: new Date(log.logged_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }),
        severity: log.severity,
        symptom: symptoms.find(s => s.id === log.symptom)?.name || 'Unknown',
      }));
  }, [symptomLogs, symptoms]);

  // ─── Active Tab Config ────────────────────────────────────────────

  const activeTrackerConfig = TRACKER_TAB_CONFIG.find(t => t.key === trackerSubTab) || TRACKER_TAB_CONFIG[0];

  // ─── Add Buttons per Tab ──────────────────────────────────────────

  const addButtons: Record<string, { type: string; label: string }> = {
    symptoms: { type: 'symptom', label: 'Add Symptom' },
    conditions: { type: 'condition', label: 'Add Condition' },
    surgeries: { type: 'surgery', label: 'Add Surgery' },
  };

  // ─── Render Helpers ─────────────────────────────────────────────────

  const formatDate = (d: string | null) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatDateTime = (d: string | null) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const renderSkeleton = () => (
    <div className="sc-skeleton-grid">
      {[0, 1, 2, 3].map(i => (
        <div key={i} className="sc-skeleton-card">
          <div className="sc-skeleton-line long" />
          <div className="sc-skeleton-line medium" />
          <div className="sc-skeleton-line short" />
        </div>
      ))}
    </div>
  );

  const renderEmpty = (title: string, desc: string, addType: string, addLabel: string) => (
    <div className="sc-empty-state">
      <div className="sc-empty-icon"><FontAwesomeIcon icon={faClipboardList} /></div>
      <h3>{title}</h3>
      <p>{desc}</p>
      <button className="sc-btn sc-btn-primary" onClick={() => openModal(addType)}>
        <FontAwesomeIcon icon={faPlus} /> {addLabel}
      </button>
    </div>
  );

  // ─── Tab: Symptoms ──────────────────────────────────────────────────

  const renderSymptoms = () => {
    if (loading) return renderSkeleton();
    if (symptoms.length === 0) return renderEmpty('No Symptoms Recorded', 'Start tracking by adding your first symptom.', 'symptom', 'Add Symptom');

    return (
      <div className="sc-cards-grid">
        {symptoms.map(s => (
          <div key={s.id} className="sc-card">
            <div className="sc-card-header">
              <div className="sc-card-icon" style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' }}>
                <FontAwesomeIcon icon={faClipboardList} />
              </div>
              <div className="sc-card-title-area">
                <h4>{s.name}</h4>
                {s.body_area && <span className="sc-card-badge">{s.body_area}</span>}
              </div>
              <span className={`sc-severity-badge sc-severity-${s.severity > 7 ? 'high' : s.severity > 4 ? 'mid' : 'low'}`}>
                {s.severity}/10
              </span>
            </div>
            {s.description && <p className="sc-card-desc">{s.description}</p>}
            <div className="sc-card-meta">
              {s.first_noticed && <span><FontAwesomeIcon icon={faCalendarDays} /> Since {formatDate(s.first_noticed)}</span>}
              <span className={`sc-status-pill ${s.is_active ? 'active' : 'inactive'}`}>{s.is_active ? 'Active' : 'Inactive'}</span>
            </div>
            <div className="sc-card-actions">
              <button className="sc-action-btn" onClick={() => openModal('symptom_log', { symptom: String(s.id) })}>
                <FontAwesomeIcon icon={faChartLine} /> Log
              </button>
              <button className="sc-action-btn" onClick={() => openModal('symptom', {
                name: s.name, description: s.description, severity: String(s.severity),
                body_area: s.body_area, first_noticed: s.first_noticed || '',
              }, s.id)}>
                <FontAwesomeIcon icon={faPen} /> Edit
              </button>
              <button className="sc-action-btn sc-action-delete" onClick={() => setConfirmDelete({ type: 'symptom', id: s.id })}>
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // ─── Tab: Conditions ────────────────────────────────────────────────

  const renderConditions = () => {
    if (loading) return renderSkeleton();
    if (conditions.length === 0) return renderEmpty('No Medical Conditions', 'Record any diagnosed medical conditions here.', 'condition', 'Add Condition');

    const statusColors: Record<string, string> = { active: '#ef4444', managed: '#f59e0b', resolved: '#10b981' };

    return (
      <div className="sc-cards-grid">
        {conditions.map(c => (
          <div key={c.id} className="sc-card">
            <div className="sc-card-header">
              <div className="sc-card-icon" style={{ background: 'rgba(236, 72, 153, 0.1)', color: '#ec4899' }}>
                <FontAwesomeIcon icon={faHeartPulse} />
              </div>
              <div className="sc-card-title-area">
                <h4>{c.name}</h4>
              </div>
              <span className="sc-status-pill" style={{ background: `${statusColors[c.status]}15`, color: statusColors[c.status] }}>
                {c.status}
              </span>
            </div>
            {c.notes && <p className="sc-card-desc">{c.notes}</p>}
            <div className="sc-card-meta">
              {c.diagnosed_date && <span><FontAwesomeIcon icon={faCalendarDays} /> Diagnosed {formatDate(c.diagnosed_date)}</span>}
            </div>
            <div className="sc-card-actions">
              <button className="sc-action-btn" onClick={() => openModal('condition', {
                name: c.name, diagnosed_date: c.diagnosed_date || '', status: c.status, notes: c.notes,
              }, c.id)}>
                <FontAwesomeIcon icon={faPen} /> Edit
              </button>
              <button className="sc-action-btn sc-action-delete" onClick={() => setConfirmDelete({ type: 'condition', id: c.id })}>
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // ─── Tab: Allergies (without diet) ─────────────────────────────────

  const renderAllergies = () => {
    if (loading) return renderSkeleton();

    return (
      <div className="sc-multi-section">
        <div className="sc-sub-section">
          <div className="sc-sub-header">
            <h3><FontAwesomeIcon icon={faAllergies} /> Allergies</h3>
            <button className="sc-btn sc-btn-sm" onClick={() => openModal('allergy')}>
              <FontAwesomeIcon icon={faPlus} /> Add
            </button>
          </div>
          {allergies.length === 0 ? (
            <p className="sc-no-data">No allergies recorded.</p>
          ) : (
            <div className="sc-list">
              {allergies.map(a => (
                <div key={a.id} className="sc-list-item">
                  <div className="sc-list-main">
                    <strong>{a.name}</strong>
                    <div className="sc-list-tags">
                      <span className="sc-tag">{a.allergy_type}</span>
                      <span className={`sc-tag sc-tag-${a.severity}`}>{a.severity}</span>
                    </div>
                    {a.reaction && <p className="sc-list-note">{a.reaction}</p>}
                  </div>
                  <div className="sc-list-actions">
                    <button className="sc-action-btn" onClick={() => openModal('allergy', {
                      name: a.name, allergy_type: a.allergy_type, severity: a.severity, reaction: a.reaction,
                    }, a.id)}><FontAwesomeIcon icon={faPen} /></button>
                    <button className="sc-action-btn sc-action-delete" onClick={() => setConfirmDelete({ type: 'allergy', id: a.id })}>
                      <FontAwesomeIcon icon={faTrash} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  // ─── Tab: Surgeries ─────────────────────────────────────────────────

  const renderSurgeries = () => {
    if (loading) return renderSkeleton();
    if (surgeries.length === 0) return renderEmpty('No Past Surgeries', 'Record any surgical procedures you have had.', 'surgery', 'Add Surgery');

    return (
      <div className="sc-cards-grid">
        {surgeries.map(s => (
          <div key={s.id} className="sc-card">
            <div className="sc-card-header">
              <div className="sc-card-icon" style={{ background: 'rgba(99, 102, 241, 0.1)', color: '#6366f1' }}>
                <FontAwesomeIcon icon={faScissors} />
              </div>
              <div className="sc-card-title-area">
                <h4>{s.name}</h4>
              </div>
            </div>
            <div className="sc-card-meta">
              {s.surgery_date && <span><FontAwesomeIcon icon={faCalendarDays} /> {formatDate(s.surgery_date)}</span>}
              {s.hospital && <span>{s.hospital}</span>}
              {s.surgeon && <span>Dr. {s.surgeon}</span>}
            </div>
            {s.notes && <p className="sc-card-desc">{s.notes}</p>}
            <div className="sc-card-actions">
              <button className="sc-action-btn" onClick={() => openModal('surgery', {
                name: s.name, surgery_date: s.surgery_date || '', hospital: s.hospital, surgeon: s.surgeon, notes: s.notes,
              }, s.id)}>
                <FontAwesomeIcon icon={faPen} /> Edit
              </button>
              <button className="sc-action-btn sc-action-delete" onClick={() => setConfirmDelete({ type: 'surgery', id: s.id })}>
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // ─── Tab: Tracking ──────────────────────────────────────────────────

  const renderTracking = () => {
    if (loading) return renderSkeleton();

    return (
      <div className="sc-tracking-section">
        <div className="sc-chart-card">
          <h3>Severity Over Time</h3>
          {chartData.length === 0 ? (
            <p className="sc-no-data">No symptom logs yet. Log a symptom severity to see trends here.</p>
          ) : (
            <div className="sc-chart-wrap">
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#64748b' }} />
                  <YAxis domain={[0, 10]} tick={{ fontSize: 12, fill: '#64748b' }} />
                  <Tooltip
                    contentStyle={{ borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 13 }}
                    formatter={(value) => [`${value}/10`, 'Severity']}
                  />
                  <Line type="monotone" dataKey="severity" stroke="#ef4444" strokeWidth={2.5} dot={{ fill: '#ef4444', r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Recent Logs */}
        <div className="sc-chart-card">
          <div className="sc-sub-header">
            <h3>Recent Symptom Logs</h3>
            {symptoms.length > 0 && (
              <button className="sc-btn sc-btn-sm" onClick={() => openModal('symptom_log', { symptom: String(symptoms[0].id) })}>
                <FontAwesomeIcon icon={faPlus} /> Log
              </button>
            )}
          </div>
          {symptomLogs.length === 0 ? (
            <p className="sc-no-data">No logs recorded.</p>
          ) : (
            <div className="sc-list">
              {symptomLogs.slice(0, 20).map(log => (
                <div key={log.id} className="sc-list-item">
                  <div className="sc-list-main">
                    <strong>{symptoms.find(s => s.id === log.symptom)?.name || 'Unknown'}</strong>
                    <div className="sc-list-tags">
                      <span className={`sc-severity-badge sc-severity-${log.severity > 7 ? 'high' : log.severity > 4 ? 'mid' : 'low'}`}>
                        {log.severity}/10
                      </span>
                      <span className="sc-list-date">{formatDate(log.logged_at)}</span>
                    </div>
                    {log.notes && <p className="sc-list-note">{log.notes}</p>}
                  </div>
                  <div className="sc-list-actions">
                    <button className="sc-action-btn sc-action-delete" onClick={() => setConfirmDelete({ type: 'symptom_log', id: log.id })}>
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  // ─── Tab: AI Categories ──────────────────────────────────────────────

  const renderCategories = () => {
    if (categoriesLoading) return renderSkeleton();

    const categoryEntries = Object.entries(categoryData);

    return (
      <div className="sc-categories-section">
        <div className="sc-categories-toolbar">
          <button
            className="sc-btn sc-btn-primary sc-categorize-btn"
            onClick={handleCategorizeAI}
            disabled={categorizingAI || symptoms.length === 0}
          >
            <FontAwesomeIcon icon={faBrain} />
            {categorizingAI ? 'Categorizing...' : 'Categorize with AI'}
          </button>
          <span className="sc-categories-count">
            {categoryEntries.length} {categoryEntries.length === 1 ? 'category' : 'categories'} found
          </span>
        </div>

        {categoryEntries.length === 0 ? (
          <div className="sc-empty-state">
            <div className="sc-empty-icon"><FontAwesomeIcon icon={faLayerGroup} /></div>
            <h3>No Categories Yet</h3>
            <p>Click "Categorize with AI" to automatically group your symptoms into health categories.</p>
          </div>
        ) : (
          <div className="sc-category-grid">
            {categoryEntries.map(([category, categorySymptoms]) => {
              const meta = getCategoryMeta(category);
              const isExpanded = expandedCategories.has(category);
              return (
                <div key={category} className={`sc-category-card ${isExpanded ? 'expanded' : ''}`}>
                  <button className="sc-category-header" onClick={() => toggleCategory(category)}>
                    <div className="sc-category-icon" style={{ background: meta.bg, color: meta.color }}>
                      <FontAwesomeIcon icon={meta.icon} />
                    </div>
                    <div className="sc-category-info">
                      <span className="sc-category-name">{category}</span>
                      <span className="sc-category-count">{categorySymptoms.length} {categorySymptoms.length === 1 ? 'symptom' : 'symptoms'}</span>
                    </div>
                    <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} className="sc-category-chevron" />
                  </button>
                  {isExpanded && (
                    <div className="sc-category-body">
                      <div className="sc-list">
                        {categorySymptoms.map(s => (
                          <div key={s.id} className="sc-list-item">
                            <div className="sc-list-main">
                              <strong>{s.name}</strong>
                              <div className="sc-list-tags">
                                {s.body_area && <span className="sc-tag">{s.body_area}</span>}
                                <span className={`sc-severity-badge sc-severity-${s.severity > 7 ? 'high' : s.severity > 4 ? 'mid' : 'low'}`}>
                                  {s.severity}/10
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // ─── Diet Tracker: Food Log ──────────────────────────────────────────

  const renderFoodLog = () => {
    if (loading) return renderSkeleton();

    return (
      <div className="sc-diet-section">
        <div className="sc-sub-section">
          <div className="sc-sub-header">
            <h3><FontAwesomeIcon icon={faUtensils} /> Food Log</h3>
            <button className="sc-btn sc-btn-sm" onClick={() => openModal('food_log')}>
              <FontAwesomeIcon icon={faPlus} /> Add
            </button>
          </div>
          {foodLogs.length === 0 ? (
            <p className="sc-no-data">No food logs recorded.</p>
          ) : (
            <div className="sc-list">
              {foodLogs.map(f => (
                <div key={f.id} className="sc-list-item">
                  <div className="sc-list-main">
                    <strong>{f.food_item}</strong>
                    <div className="sc-list-tags">
                      <span className="sc-tag">{f.meal_type}</span>
                      <span className="sc-list-date">{formatDate(f.logged_at)}</span>
                    </div>
                    {f.notes && <p className="sc-list-note">{f.notes}</p>}
                  </div>
                  <div className="sc-list-actions">
                    <button className="sc-action-btn sc-action-delete" onClick={() => setConfirmDelete({ type: 'food_log', id: f.id })}>
                      <FontAwesomeIcon icon={faTrash} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  // ─── Diet Tracker: Dietary Restrictions ──────────────────────────────

  const renderDietaryRestrictions = () => {
    if (loading) return renderSkeleton();

    return (
      <div className="sc-diet-section">
        <div className="sc-sub-section">
          <div className="sc-sub-header">
            <h3><FontAwesomeIcon icon={faSeedling} /> Dietary Restrictions</h3>
            <button className="sc-btn sc-btn-sm" onClick={() => openModal('dietary')}>
              <FontAwesomeIcon icon={faPlus} /> Add
            </button>
          </div>
          {dietaryRestrictions.length === 0 ? (
            <p className="sc-no-data">No dietary restrictions recorded.</p>
          ) : (
            <div className="sc-list">
              {dietaryRestrictions.map(d => (
                <div key={d.id} className="sc-list-item">
                  <div className="sc-list-main">
                    <strong>{d.name}</strong>
                    <span className="sc-tag">{d.restriction_type}</span>
                    {d.notes && <p className="sc-list-note">{d.notes}</p>}
                  </div>
                  <div className="sc-list-actions">
                    <button className="sc-action-btn" onClick={() => openModal('dietary', {
                      name: d.name, restriction_type: d.restriction_type, notes: d.notes,
                    }, d.id)}><FontAwesomeIcon icon={faPen} /></button>
                    <button className="sc-action-btn sc-action-delete" onClick={() => setConfirmDelete({ type: 'dietary', id: d.id })}>
                      <FontAwesomeIcon icon={faTrash} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  // ─── Reminders Tab ──────────────────────────────────────────────────

  const getFilteredReminders = (): Reminder[] => {
    switch (reminderSubTab) {
      case 'upcoming': return reminders.filter(r => r.status === 'upcoming');
      case 'today': return reminders.filter(r => r.status === 'today');
      case 'completed': return reminders.filter(r => r.is_completed);
      default: return reminders;
    }
  };

  const getPriorityColor = (priority: string): string => {
    switch (priority) {
      case 'high': return '#ef4444';
      case 'medium': return '#f59e0b';
      case 'low': return '#10b981';
      default: return '#64748b';
    }
  };

  const renderReminders = () => {
    if (remindersLoading) return renderSkeleton();

    const filtered = getFilteredReminders();

    return (
      <div className="sc-reminders-section">
        {filtered.length === 0 ? (
          <div className="sc-empty-state">
            <div className="sc-empty-icon"><FontAwesomeIcon icon={faBell} /></div>
            <h3>No Reminders</h3>
            <p>{reminderSubTab === 'all' ? 'Create your first reminder to stay on top of your health.' : `No ${reminderSubTab} reminders found.`}</p>
            <button className="sc-btn sc-btn-primary" onClick={() => openModal('reminder')}>
              <FontAwesomeIcon icon={faPlus} /> Add Reminder
            </button>
          </div>
        ) : (
          <div className="sc-cards-grid">
            {filtered.map(r => (
              <div key={r.id} className="sc-reminder-card">
                <div className="sc-card-header">
                  <div className="sc-card-icon" style={{ background: `${getPriorityColor(r.priority)}12`, color: getPriorityColor(r.priority) }}>
                    <FontAwesomeIcon icon={faBell} />
                  </div>
                  <div className="sc-card-title-area">
                    <h4>{r.title}</h4>
                    <div className="sc-list-tags" style={{ marginTop: 4 }}>
                      <span className="sc-tag">{r.category}</span>
                      <span className="sc-tag" style={{ background: `${getPriorityColor(r.priority)}15`, color: getPriorityColor(r.priority) }}>
                        {r.priority}
                      </span>
                    </div>
                  </div>
                  <span className={`sc-status-pill ${r.is_completed ? 'inactive' : 'active'}`}>
                    {r.is_completed ? 'Done' : r.status}
                  </span>
                </div>
                {r.description && <p className="sc-card-desc">{r.description}</p>}
                <div className="sc-card-meta">
                  <span><FontAwesomeIcon icon={faClock} /> {formatDateTime(r.reminder_datetime)}</span>
                  {r.recurrence && r.recurrence !== 'none' && <span>Repeats: {r.recurrence}</span>}
                </div>
                {r.notes && <p className="sc-card-desc" style={{ fontSize: '0.78rem', color: '#94a3b8' }}>{r.notes}</p>}
                <div className="sc-card-actions">
                  {!r.is_completed && (
                    <button className="sc-action-btn" onClick={() => handleCompleteReminder(r.id)}>
                      <FontAwesomeIcon icon={faCircleCheck} /> Complete
                    </button>
                  )}
                  <button className="sc-action-btn" onClick={() => openModal('reminder', {
                    title: r.title,
                    description: r.description || '',
                    reminder_datetime: r.reminder_datetime ? r.reminder_datetime.slice(0, 16) : '',
                    category: r.category || '',
                    priority: r.priority || 'medium',
                    recurrence: r.recurrence || 'none',
                    notes: r.notes || '',
                  }, r.id)}>
                    <FontAwesomeIcon icon={faPen} /> Edit
                  </button>
                  <button className="sc-action-btn sc-action-delete" onClick={() => setConfirmDelete({ type: 'reminder', id: r.id })}>
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // ─── Modal Renderer ─────────────────────────────────────────────────

  const renderModal = () => {
    if (!modalType) return null;

    const titles: Record<string, string> = {
      symptom: editingId ? 'Edit Symptom' : 'Add Symptom',
      symptom_log: 'Log Symptom Severity',
      condition: editingId ? 'Edit Condition' : 'Add Condition',
      allergy: editingId ? 'Edit Allergy' : 'Add Allergy',
      surgery: editingId ? 'Edit Surgery' : 'Add Surgery',
      dietary: editingId ? 'Edit Dietary Restriction' : 'Add Dietary Restriction',
      food_log: 'Log Food',
      reminder: editingId ? 'Edit Reminder' : 'Add Reminder',
    };

    return (
      <div className="sc-modal-overlay" onClick={closeModal}>
        <div className="sc-modal" onClick={e => e.stopPropagation()}>
          <div className="sc-modal-header">
            <h3>{titles[modalType]}</h3>
            <button className="sc-modal-close" onClick={closeModal}><FontAwesomeIcon icon={faTimes} /></button>
          </div>
          <div className="sc-modal-body">
            {modalType === 'symptom' && (
              <>
                <label className="sc-form-label">Name <span className="sc-required">*</span></label>
                <input className="sc-form-input" placeholder="e.g. Headache" value={modalData.name || ''} onChange={e => handleModalField('name', e.target.value)} />
                <label className="sc-form-label">Description</label>
                <textarea className="sc-form-textarea" placeholder="Describe the symptom..." rows={2} value={modalData.description || ''} onChange={e => handleModalField('description', e.target.value)} />
                <div className="sc-form-row">
                  <div className="sc-form-col">
                    <label className="sc-form-label">Severity (1-10)</label>
                    <input className="sc-form-input" type="number" min="1" max="10" value={modalData.severity || '5'} onChange={e => handleModalField('severity', e.target.value)} />
                  </div>
                  <div className="sc-form-col">
                    <label className="sc-form-label">Body Area</label>
                    <input className="sc-form-input" placeholder="e.g. Head" value={modalData.body_area || ''} onChange={e => handleModalField('body_area', e.target.value)} />
                  </div>
                </div>
                <label className="sc-form-label">First Noticed</label>
                <input className="sc-form-input" type="date" value={modalData.first_noticed || ''} onChange={e => handleModalField('first_noticed', e.target.value)} />
              </>
            )}
            {modalType === 'symptom_log' && (
              <>
                <label className="sc-form-label">Symptom <span className="sc-required">*</span></label>
                <select className="sc-form-input" value={modalData.symptom || ''} onChange={e => handleModalField('symptom', e.target.value)}>
                  <option value="">Select symptom...</option>
                  {symptoms.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <label className="sc-form-label">Severity (1-10)</label>
                <input className="sc-form-input" type="number" min="1" max="10" value={modalData.severity || '5'} onChange={e => handleModalField('severity', e.target.value)} />
                <label className="sc-form-label">Notes</label>
                <textarea className="sc-form-textarea" placeholder="Any notes..." rows={2} value={modalData.notes || ''} onChange={e => handleModalField('notes', e.target.value)} />
              </>
            )}
            {modalType === 'condition' && (
              <>
                <label className="sc-form-label">Name <span className="sc-required">*</span></label>
                <input className="sc-form-input" placeholder="e.g. Diabetes Type 2" value={modalData.name || ''} onChange={e => handleModalField('name', e.target.value)} />
                <div className="sc-form-row">
                  <div className="sc-form-col">
                    <label className="sc-form-label">Diagnosed Date</label>
                    <input className="sc-form-input" type="date" value={modalData.diagnosed_date || ''} onChange={e => handleModalField('diagnosed_date', e.target.value)} />
                  </div>
                  <div className="sc-form-col">
                    <label className="sc-form-label">Status</label>
                    <select className="sc-form-input" value={modalData.status || 'active'} onChange={e => handleModalField('status', e.target.value)}>
                      <option value="active">Active</option>
                      <option value="managed">Managed</option>
                      <option value="resolved">Resolved</option>
                    </select>
                  </div>
                </div>
                <label className="sc-form-label">Notes</label>
                <textarea className="sc-form-textarea" placeholder="Any notes..." rows={2} value={modalData.notes || ''} onChange={e => handleModalField('notes', e.target.value)} />
              </>
            )}
            {modalType === 'allergy' && (
              <>
                <label className="sc-form-label">Name <span className="sc-required">*</span></label>
                <input className="sc-form-input" placeholder="e.g. Peanuts" value={modalData.name || ''} onChange={e => handleModalField('name', e.target.value)} />
                <div className="sc-form-row">
                  <div className="sc-form-col">
                    <label className="sc-form-label">Type</label>
                    <select className="sc-form-input" value={modalData.allergy_type || 'other'} onChange={e => handleModalField('allergy_type', e.target.value)}>
                      <option value="food">Food</option>
                      <option value="drug">Drug</option>
                      <option value="environmental">Environmental</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="sc-form-col">
                    <label className="sc-form-label">Severity</label>
                    <select className="sc-form-input" value={modalData.severity || 'moderate'} onChange={e => handleModalField('severity', e.target.value)}>
                      <option value="mild">Mild</option>
                      <option value="moderate">Moderate</option>
                      <option value="severe">Severe</option>
                    </select>
                  </div>
                </div>
                <label className="sc-form-label">Reaction</label>
                <textarea className="sc-form-textarea" placeholder="Describe the reaction..." rows={2} value={modalData.reaction || ''} onChange={e => handleModalField('reaction', e.target.value)} />
              </>
            )}
            {modalType === 'surgery' && (
              <>
                <label className="sc-form-label">Name <span className="sc-required">*</span></label>
                <input className="sc-form-input" placeholder="e.g. Appendectomy" value={modalData.name || ''} onChange={e => handleModalField('name', e.target.value)} />
                <div className="sc-form-row">
                  <div className="sc-form-col">
                    <label className="sc-form-label">Date</label>
                    <input className="sc-form-input" type="date" value={modalData.surgery_date || ''} onChange={e => handleModalField('surgery_date', e.target.value)} />
                  </div>
                  <div className="sc-form-col">
                    <label className="sc-form-label">Hospital</label>
                    <input className="sc-form-input" placeholder="Hospital name" value={modalData.hospital || ''} onChange={e => handleModalField('hospital', e.target.value)} />
                  </div>
                </div>
                <label className="sc-form-label">Surgeon</label>
                <input className="sc-form-input" placeholder="Surgeon name" value={modalData.surgeon || ''} onChange={e => handleModalField('surgeon', e.target.value)} />
                <label className="sc-form-label">Notes</label>
                <textarea className="sc-form-textarea" placeholder="Any notes..." rows={2} value={modalData.notes || ''} onChange={e => handleModalField('notes', e.target.value)} />
              </>
            )}
            {modalType === 'dietary' && (
              <>
                <label className="sc-form-label">Name <span className="sc-required">*</span></label>
                <input className="sc-form-input" placeholder="e.g. Lactose Free" value={modalData.name || ''} onChange={e => handleModalField('name', e.target.value)} />
                <label className="sc-form-label">Type</label>
                <select className="sc-form-input" value={modalData.restriction_type || 'preference'} onChange={e => handleModalField('restriction_type', e.target.value)}>
                  <option value="allergy">Allergy</option>
                  <option value="intolerance">Intolerance</option>
                  <option value="preference">Preference</option>
                  <option value="medical">Medical</option>
                </select>
                <label className="sc-form-label">Notes</label>
                <textarea className="sc-form-textarea" placeholder="Any notes..." rows={2} value={modalData.notes || ''} onChange={e => handleModalField('notes', e.target.value)} />
              </>
            )}
            {modalType === 'food_log' && (
              <>
                <label className="sc-form-label">Food Item <span className="sc-required">*</span></label>
                <input className="sc-form-input" placeholder="e.g. Grilled chicken salad" value={modalData.food_item || ''} onChange={e => handleModalField('food_item', e.target.value)} />
                <label className="sc-form-label">Meal Type</label>
                <select className="sc-form-input" value={modalData.meal_type || 'lunch'} onChange={e => handleModalField('meal_type', e.target.value)}>
                  <option value="breakfast">Breakfast</option>
                  <option value="lunch">Lunch</option>
                  <option value="dinner">Dinner</option>
                  <option value="snack">Snack</option>
                </select>
                <label className="sc-form-label">Notes</label>
                <textarea className="sc-form-textarea" placeholder="Any notes..." rows={2} value={modalData.notes || ''} onChange={e => handleModalField('notes', e.target.value)} />
              </>
            )}
            {modalType === 'reminder' && (
              <>
                <label className="sc-form-label">Title <span className="sc-required">*</span></label>
                <input className="sc-form-input" placeholder="e.g. Take morning medication" value={modalData.title || ''} onChange={e => handleModalField('title', e.target.value)} />
                <label className="sc-form-label">Description</label>
                <textarea className="sc-form-textarea" placeholder="Describe the reminder..." rows={2} value={modalData.description || ''} onChange={e => handleModalField('description', e.target.value)} />
                <div className="sc-form-row">
                  <div className="sc-form-col">
                    <label className="sc-form-label">Date & Time <span className="sc-required">*</span></label>
                    <input className="sc-form-input" type="datetime-local" value={modalData.reminder_datetime || ''} onChange={e => handleModalField('reminder_datetime', e.target.value)} />
                  </div>
                  <div className="sc-form-col">
                    <label className="sc-form-label">Category</label>
                    <select className="sc-form-input" value={modalData.category || 'general'} onChange={e => handleModalField('category', e.target.value)}>
                      <option value="general">General</option>
                      <option value="medication">Medication</option>
                      <option value="appointment">Appointment</option>
                      <option value="exercise">Exercise</option>
                      <option value="diet">Diet</option>
                      <option value="checkup">Checkup</option>
                    </select>
                  </div>
                </div>
                <div className="sc-form-row">
                  <div className="sc-form-col">
                    <label className="sc-form-label">Priority</label>
                    <select className="sc-form-input" value={modalData.priority || 'medium'} onChange={e => handleModalField('priority', e.target.value)}>
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                  <div className="sc-form-col">
                    <label className="sc-form-label">Recurrence</label>
                    <select className="sc-form-input" value={modalData.recurrence || 'none'} onChange={e => handleModalField('recurrence', e.target.value)}>
                      <option value="none">None</option>
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>
                </div>
                <label className="sc-form-label">Notes</label>
                <textarea className="sc-form-textarea" placeholder="Any additional notes..." rows={2} value={modalData.notes || ''} onChange={e => handleModalField('notes', e.target.value)} />
              </>
            )}
          </div>
          <div className="sc-modal-footer">
            <button className="sc-btn sc-btn-cancel" onClick={closeModal}>Cancel</button>
            <button className="sc-btn sc-btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : <><FontAwesomeIcon icon={faCheck} /> {editingId ? 'Update' : 'Save'}</>}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ─── Determine what "Add New" does based on current primary/sub tab ─

  const handleAddNew = () => {
    if (primaryTab === 'tracker') {
      const btn = addButtons[trackerSubTab];
      if (btn) openModal(btn.type);
      else openModal('symptom');
    } else if (primaryTab === 'diet') {
      if (dietSubTab === 'food_log') openModal('food_log');
      else openModal('dietary');
    } else if (primaryTab === 'reminders') {
      openModal('reminder');
    }
  };

  // ─── Render Tracker Content ─────────────────────────────────────────

  const trackerContent: Record<TrackerSubTab, () => React.ReactNode> = {
    symptoms: renderSymptoms,
    conditions: renderConditions,
    allergies: renderAllergies,
    surgeries: renderSurgeries,
    tracking: renderTracking,
    categories: renderCategories,
  };

  // ─── Diet sub-tab config ────────────────────────────────────────────

  const DIET_TAB_CONFIG = [
    { key: 'food_log' as DietSubTab, label: 'Food Log', icon: faUtensils, color: '#f59e0b' },
    { key: 'restrictions' as DietSubTab, label: 'Dietary Restrictions', icon: faSeedling, color: '#10b981' },
  ];

  // ─── Reminder sub-tab config ────────────────────────────────────────

  const REMINDER_TAB_CONFIG = [
    { key: 'all' as ReminderSubTab, label: 'All', icon: faListCheck, color: '#06b6d4' },
    { key: 'upcoming' as ReminderSubTab, label: 'Upcoming', icon: faClock, color: '#8b5cf6' },
    { key: 'today' as ReminderSubTab, label: 'Today', icon: faCalendarDays, color: '#f59e0b' },
    { key: 'completed' as ReminderSubTab, label: 'Completed', icon: faCircleCheck, color: '#10b981' },
  ];

  // ─── Main Render ────────────────────────────────────────────────────

  return (
    <div className="sc-page">
      {/* Toast */}
      {toast && (
        <div className={`sc-toast sc-toast-${toast.type}`}>
          <FontAwesomeIcon icon={toast.type === 'success' ? faCheck : faExclamationTriangle} />
          {toast.message}
        </div>
      )}

      {/* Delete Confirmation */}
      {confirmDelete && (
        <div className="sc-modal-overlay" onClick={() => setConfirmDelete(null)}>
          <div className="sc-modal sc-modal-sm" onClick={e => e.stopPropagation()}>
            <div className="sc-confirm-icon"><FontAwesomeIcon icon={faTrash} /></div>
            <h3>Delete this item?</h3>
            <p>This action cannot be undone.</p>
            <div className="sc-modal-footer">
              <button className="sc-btn sc-btn-cancel" onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button className="sc-btn sc-btn-danger" onClick={handleDelete}>
                <FontAwesomeIcon icon={faTrash} /> Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      {renderModal()}

      {/* ══════════ WELCOME BANNER (Navy Gradient) ══════════ */}
      <section className="sc-welcome-banner">
        <Container>
          <BackButton />
          <div className="sc-welcome-layout">
            <div className="sc-welcome-text">
              <h1 className="sc-welcome-title">
                <FontAwesomeIcon icon={faStethoscope} />
                Symptoms Tracker
              </h1>
              <p className="sc-welcome-subtitle">
                Track your symptoms, manage your diet, and set health reminders — all in one place.
              </p>
            </div>
            <div className="sc-welcome-actions">
              <button className="sc-welcome-btn primary" onClick={handleAddNew}>
                <FontAwesomeIcon icon={faPlus} />
                Add New
              </button>
            </div>
          </div>
        </Container>
      </section>

      {/* ══════════ PRIMARY TABS ══════════ */}
      <section className="sc-primary-tabs">
        <Container>
          <div className="sc-primary-tabs-row">
            {([
              { key: 'tracker' as PrimaryTab, label: 'Symptoms Tracker', icon: faStethoscope, color: '#ef4444' },
              { key: 'diet' as PrimaryTab, label: 'Diet Tracker', icon: faUtensils, color: '#f59e0b' },
              { key: 'reminders' as PrimaryTab, label: 'Reminders', icon: faBell, color: '#06b6d4' },
            ]).map(tab => (
              <button
                key={tab.key}
                className={`sc-primary-tab-btn ${primaryTab === tab.key ? 'active' : ''}`}
                style={{ '--primary-color': tab.color } as React.CSSProperties}
                onClick={() => setPrimaryTab(tab.key)}
              >
                <span className="sc-primary-tab-icon">
                  <FontAwesomeIcon icon={tab.icon} />
                </span>
                <span className="sc-primary-tab-label">{tab.label}</span>
              </button>
            ))}
          </div>
        </Container>
      </section>

      {/* ══════════ STATS ROW (context-sensitive) ══════════ */}
      <section className="sc-stats-section">
        <Container>
          <div className="sc-stats-row">
            {primaryTab === 'tracker' && ([
              { icon: faClipboardList, color: '#ef4444', value: trackerStats.symptoms, label: 'Symptoms' },
              { icon: faHeartPulse, color: '#ec4899', value: trackerStats.conditions, label: 'Active Conditions' },
              { icon: faAllergies, color: '#f59e0b', value: trackerStats.allergies, label: 'Allergies' },
              { icon: faScissors, color: '#6366f1', value: trackerStats.surgeries, label: 'Surgeries' },
            ] as { icon: IconDefinition; color: string; value: number; label: string }[]).map((item, idx) => (
              <div className="sc-stat-card" key={idx}>
                <div className="sc-stat-icon" style={{ background: `${item.color}12`, color: item.color }}>
                  <FontAwesomeIcon icon={item.icon} />
                </div>
                <div className="sc-stat-info">
                  <span className="sc-stat-value">{item.value}</span>
                  <span className="sc-stat-label">{item.label}</span>
                </div>
              </div>
            ))}
            {primaryTab === 'diet' && ([
              { icon: faUtensils, color: '#f59e0b', value: dietStats.foodLogsToday, label: 'Logged Today' },
              { icon: faSeedling, color: '#10b981', value: dietStats.totalRestrictions, label: 'Restrictions' },
              { icon: faClipboardList, color: '#8b5cf6', value: dietStats.mealsLogged, label: 'Total Meals' },
              { icon: faCalendarDays, color: '#06b6d4', value: foodLogs.length > 0 ? Math.ceil(new Set(foodLogs.map(f => new Date(f.logged_at).toDateString())).size) : 0, label: 'Days Tracked' },
            ] as { icon: IconDefinition; color: string; value: number; label: string }[]).map((item, idx) => (
              <div className="sc-stat-card" key={idx}>
                <div className="sc-stat-icon" style={{ background: `${item.color}12`, color: item.color }}>
                  <FontAwesomeIcon icon={item.icon} />
                </div>
                <div className="sc-stat-info">
                  <span className="sc-stat-value">{item.value}</span>
                  <span className="sc-stat-label">{item.label}</span>
                </div>
              </div>
            ))}
            {primaryTab === 'reminders' && ([
              { icon: faBell, color: '#06b6d4', value: reminderStats.total, label: 'Total' },
              { icon: faClock, color: '#8b5cf6', value: reminderStats.upcoming, label: 'Upcoming' },
              { icon: faCalendarDays, color: '#f59e0b', value: reminderStats.today, label: 'Today' },
              { icon: faCircleCheck, color: '#10b981', value: reminderStats.completed, label: 'Completed' },
            ] as { icon: IconDefinition; color: string; value: number; label: string }[]).map((item, idx) => (
              <div className="sc-stat-card" key={idx}>
                <div className="sc-stat-icon" style={{ background: `${item.color}12`, color: item.color }}>
                  <FontAwesomeIcon icon={item.icon} />
                </div>
                <div className="sc-stat-info">
                  <span className="sc-stat-value">{item.value}</span>
                  <span className="sc-stat-label">{item.label}</span>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* ══════════ TAB CONTENT ══════════ */}
      <Container>
        <div className="sc-tab-content">

          {/* ── TRACKER PRIMARY TAB ── */}
          {primaryTab === 'tracker' && (
            <>
              <div className={`sc-tab-header ${activeTrackerConfig.headerClass}`}>
                <div className="sc-tab-header-top">
                  <div className="sc-tab-header-text">
                    <h2>
                      <FontAwesomeIcon icon={activeTrackerConfig.icon} className={`sc-tab-header-icon ${activeTrackerConfig.iconClass}`} />
                      {activeTrackerConfig.headerTitle}
                    </h2>
                    <p>{activeTrackerConfig.headerDesc}</p>
                  </div>
                  {addButtons[trackerSubTab] && (
                    <button className="sc-btn sc-btn-primary sc-header-add-btn" onClick={() => openModal(addButtons[trackerSubTab].type)}>
                      <FontAwesomeIcon icon={faPlus} /> {addButtons[trackerSubTab].label}
                    </button>
                  )}
                </div>
                <div className="sc-tabs-container" role="tablist">
                  {TRACKER_TAB_CONFIG.map(tab => (
                    <button
                      key={tab.key}
                      className={`sc-tab-btn ${trackerSubTab === tab.key ? 'active' : ''}`}
                      style={trackerSubTab === tab.key ? { '--tab-color': tab.color } as React.CSSProperties : {}}
                      onClick={() => setTrackerSubTab(tab.key)}
                      role="tab"
                      aria-selected={trackerSubTab === tab.key}
                    >
                      <FontAwesomeIcon icon={tab.icon} className="sc-tab-icon" />
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="sc-active-content">
                {trackerContent[trackerSubTab]?.()}
              </div>
            </>
          )}

          {/* ── DIET PRIMARY TAB ── */}
          {primaryTab === 'diet' && (
            <>
              <div className="sc-tab-header sc-tab-header-diet">
                <div className="sc-tab-header-top">
                  <div className="sc-tab-header-text">
                    <h2>
                      <FontAwesomeIcon icon={faUtensils} className="sc-tab-header-icon sc-icon-diet" />
                      {dietSubTab === 'food_log' ? 'Food Log' : 'Dietary Restrictions'}
                    </h2>
                    <p>
                      {dietSubTab === 'food_log'
                        ? 'Track what you eat throughout the day. Log meals and snacks for a complete dietary picture.'
                        : 'Manage your dietary restrictions, intolerances, and food preferences.'}
                    </p>
                  </div>
                  <button className="sc-btn sc-btn-primary sc-header-add-btn" onClick={() => openModal(dietSubTab === 'food_log' ? 'food_log' : 'dietary')}>
                    <FontAwesomeIcon icon={faPlus} /> {dietSubTab === 'food_log' ? 'Log Food' : 'Add Restriction'}
                  </button>
                </div>
                <div className="sc-tabs-container" role="tablist">
                  {DIET_TAB_CONFIG.map(tab => (
                    <button
                      key={tab.key}
                      className={`sc-tab-btn ${dietSubTab === tab.key ? 'active' : ''}`}
                      style={dietSubTab === tab.key ? { '--tab-color': tab.color } as React.CSSProperties : {}}
                      onClick={() => setDietSubTab(tab.key)}
                      role="tab"
                      aria-selected={dietSubTab === tab.key}
                    >
                      <FontAwesomeIcon icon={tab.icon} className="sc-tab-icon" />
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="sc-active-content">
                {dietSubTab === 'food_log' ? renderFoodLog() : renderDietaryRestrictions()}
              </div>
            </>
          )}

          {/* ── REMINDERS PRIMARY TAB ── */}
          {primaryTab === 'reminders' && (
            <>
              <div className="sc-tab-header sc-tab-header-reminders">
                <div className="sc-tab-header-top">
                  <div className="sc-tab-header-text">
                    <h2>
                      <FontAwesomeIcon icon={faBell} className="sc-tab-header-icon sc-icon-reminders" />
                      Health Reminders
                    </h2>
                    <p>Stay on top of medications, appointments, and health routines with timely reminders.</p>
                  </div>
                  <button className="sc-btn sc-btn-primary sc-header-add-btn" onClick={() => openModal('reminder')}>
                    <FontAwesomeIcon icon={faPlus} /> Add Reminder
                  </button>
                </div>
                <div className="sc-tabs-container" role="tablist">
                  {REMINDER_TAB_CONFIG.map(tab => (
                    <button
                      key={tab.key}
                      className={`sc-tab-btn ${reminderSubTab === tab.key ? 'active' : ''}`}
                      style={reminderSubTab === tab.key ? { '--tab-color': tab.color } as React.CSSProperties : {}}
                      onClick={() => setReminderSubTab(tab.key)}
                      role="tab"
                      aria-selected={reminderSubTab === tab.key}
                    >
                      <FontAwesomeIcon icon={tab.icon} className="sc-tab-icon" />
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="sc-active-content">
                {renderReminders()}
              </div>
            </>
          )}

        </div>
      </Container>
    </div>
  );
};

export default SymptomsChecker;
